import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from "../lib/api";
import './EventAnketo.css';

const EventAnketo = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({}); 
    
    const [canAccess, setCanAccess] = useState(false);
    const [isEventCompleted, setIsEventCompleted] = useState(false);

    useEffect(() => {
        const verifyAccess = async () => {
            try {
                const res = await api.get(`/events/${eventId}/check_anketo_access/`);
                if (!res.data.can_access) {
                    alert(res.data.message);
                    navigate(`/event/${eventId}`); // アンケートアクセス不可の場合、イベント詳細ページにリダイレクト
                } else {
                    setCanAccess(true);
                }
            } catch (error) {
                console.error("アクセスエラーが発生しました:", error);
            }
        };
        verifyAccess();
    }, [eventId]);

    useEffect(() => {
        const fetchEventData = async () => {
            try {
                const response = await api.get(`/events/${eventId}/`);
                setEvent(response.data);
                
                const eventData = response.data;

                if (!eventData.hasAnketo) {
                    alert("このイベントにはアンケートがありません。");
                    navigate('/events');
                    return;
                }

                // endTime は ISO datetime 文字列（例: "2026-04-17T15:00:00Z"）
                const now = new Date();
                const eventEnd = eventData.endTime ? new Date(eventData.endTime) : null;
                // endTime がない場合は eventDate の終わり（23:59）で判断
                const fallbackEnd = eventData.eventDate
                    ? new Date(`${eventData.eventDate}T23:59:59`)
                    : null;
                const completed = eventEnd
                    ? now >= eventEnd
                    : fallbackEnd
                    ? now >= fallbackEnd
                    : true;
                setIsEventCompleted(completed);
            } catch (error) {
                console.error("Error fetching event data:", error);
                alert("イベント情報の取得に失敗しました。");
            } finally {
                setLoading(false);
            }
        };
        fetchEventData();
    }, [eventId, navigate]);

   
    const handleInputChange = (index, value) => {
        setAnswers(prev => ({
            ...prev,
            [index]: value
        }));
    };

    //一時保存アクション（Draft保存用）
    const handleSaveDraft = async () => {
        try {
            await api.post(`/events/${eventId}/submit_anketo/`, { 
                responses: answers,
                is_draft: true // Gửi thêm flag này lên Backend
            });
            alert("一時保存が完了しました！");
        } catch (error) {
            alert("一時保存に失敗しました。");
        }
    };
    // ページロード時に既存の回答（Draftも含む）を取得してフォームに反映
    useEffect(() => {
        const fetchSavedData = async () => {
            try {
                const res = await api.get(`/events/${eventId}/get_my_response/`);
                if (res.data && res.data.answers) {
                    setAnswers(res.data.answers); 
                }
            } catch (e) { console.log("一時保存されたデータがありません"); }
        };
        fetchSavedData();
    }, [eventId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (Object.keys(answers).length === 0) {
            alert("回答を入力してください。");
            return;
        }

        try {
            await api.post(`/events/${eventId}/submit_anketo/`, {
                responses: answers 
            });
            alert("アンケートを送信しました。ご協力ありがとうございました！");
            navigate('/events');
        } catch (error) {
            console.error("Submit error:", error);
            alert(error.response?.data?.error || "送信に失敗しました。");
        }
    };

    if (loading) return <div className="loader">読み込み中...</div>;
    if (!event) return <div>イベントが見つかりません。</div>;

    const allQuestions = Array.isArray(event.anketo_questions) ? event.anketo_questions : [];
    if (!canAccess) return <div className="loader">アクセス権限を確認しています...</div>;

    return (
        <div className="anketo-page-container">
            <div className="anketo-card">
                <h2 className="anketo-title">【アンケート】{event.eventName}</h2>
                <p className="anketo-subtitle">ご意見をお聞かせください。</p>

                {/* 未開催時のお知らせバナー */}
                {!isEventCompleted && (
                    <div style={{
                        background: "#fef3c7",
                        border: "1px solid #fcd34d",
                        borderRadius: 8,
                        padding: "10px 16px",
                        marginBottom: 16,
                        color: "#92400e",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                    }}>
                        ⚠ このイベントはまだ終了していないため、アンケートへの回答はできません。質問のみ閲覧できます。
                    </div>
                )}

                <hr />

                <form onSubmit={handleSubmit}>
                    {allQuestions.length === 0 ? (
                        <p style={{ textAlign: "center", color: "#888" }}>質問が設定されていません。</p>
                    ) : (
                        allQuestions.map((q, index) => {
                            const isObj = q && typeof q === "object" && !Array.isArray(q);
                            const questionText = isObj ? (q.question || "") : String(q || "");
                            const options = isObj && Array.isArray(q.options) ? q.options : [];
                            const current = answers[index] || "";
                            return (
                                <div key={index} className="anketo-question-box">
                                    <div className="anketo-question-title">
                                        <span className="anketo-qno" aria-hidden>{`Q${index + 1}`}</span>
                                        <span>{questionText}</span>
                                    </div>
                                    {options.length > 0 ? (
                                        <div className="anketo-options">
                                            {options.map((opt) => (
                                                <label key={opt} className={`anketo-option${!isEventCompleted ? " anketo-option-disabled" : ""}`}>
                                                    <input
                                                        type="radio"
                                                        name={`q_${index}`}
                                                        value={opt}
                                                        checked={current === opt}
                                                        onChange={() => isEventCompleted && handleInputChange(index, opt)}
                                                        disabled={!isEventCompleted}
                                                    />
                                                    <span className="anketo-option-text">{opt}</span>
                                                </label>
                                            ))}
                                        </div>
                                    ) : (
                                        <textarea
                                            className="anketo-textarea"
                                            rows="4"
                                            placeholder={isEventCompleted ? "ここに入力してください..." : "（イベント終了後に回答できます）"}
                                            value={current}
                                            onChange={(e) => isEventCompleted && handleInputChange(index, e.target.value)}
                                            disabled={!isEventCompleted}
                                            style={!isEventCompleted ? { background: "#f3f4f6", color: "#9ca3af" } : {}}
                                        />
                                    )}
                                </div>
                            );
                        })
                    )}
                    <div className="anketo-btn-group">
                        {isEventCompleted && (
                            <>
                                <button type="submit" className="btn btn-primary">
                                    送信
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-warning"
                                    onClick={handleSaveDraft}
                                >
                                    一時保存
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(`/event/${eventId}`)}
                        >
                            戻る
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EventAnketo;